import { useEffect, useState } from "react";
import { X, Plus, Loader2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import DateInput from "../DateInput";
import CheckoutFields from "../vehicle-detail/CheckoutFields";
import PortFerryCard from "./PortFerryCard";
import { toast } from "sonner";

/**
 * Admin-only modal for manually creating a reservation. Wraps the
 * `POST /api/bookings/admin/manual` endpoint, which reuses the same
 * pricing engine as the customer flow. All amounts (subtotal, tax, total)
 * are computed server-side — the modal only shows a live preview.
 *
 * Required data loaded on open:
 *  - vehicles         → /api/vehicles           (car group selector)
 *  - locations        → /api/settings/locations (pickup / dropoff)
 *  - sources          → /api/settings/public.booking_sources || defaults
 */

const PAYMENT_STATUSES = [
  { value: "unpaid",  label: "Pending" },
  { value: "paid",    label: "Paid" },
  { value: "partial", label: "Partial" },
  { value: "cod",     label: "Cash on Delivery" },
];

const PAYMENT_METHODS = [
  { value: "",                label: "— None —" },
  { value: "cash",            label: "Cash" },
  { value: "bank_transfer",   label: "Bank Transfer" },
  { value: "card_on_office",  label: "Card on Office" },
  { value: "paypal",          label: "PayPal" },
  { value: "stripe",          label: "Stripe" },
];

const DEFAULT_SOURCES = ["internet", "ai_agent", "direct", "agency", "hotel", "other"];

const EMPTY_FORM = {
  // customer
  full_name:     "",
  email:         "",
  phone:         "",
  id_passport:   "",
  license_number: "",
  driver_age:    "",
  address:       "",
  country:       "",
  city:          "",
  zip:           "",
  // conditional (airport / port / hotel)
  airline_company:       "",
  flight_number:         "",
  flight_time:           "",
  vessel_company:        "",
  vessel_name:           "",
  vessel_arrival_time:   "",
  accommodation_name:    "",
  accommodation_address: "",
  // v3.7.1 — Port pickup ferry-details card. Rendered when
  // pickup_location.toLowerCase() === "port".
  ferry_details: { company: "", vessel_name: "", arrival_time: "" },
  // rental
  vehicle_id:      "",
  pickup_location: "",
  return_location: "",
  pickup_date:     "",
  return_date:     "",
  pickup_time:     "10:00",
  return_time:     "10:00",
  addons:          [],
  // pricing / payment
  source:          "direct",
  payment_status:  "paid",
  payment_method:  "cash",
  initial_status:  "confirmed",
  notes:           "",
  // optional admin override for the deposit amount actually collected
  // right now (walk-in half-cash cases). Empty string ⇒ falls back to
  // system logic (paid → full total, partial → advance_deposit).
  deposit_override: "",
};

export default function ManualBookingModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [vehicles, setVehicles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [sources, setSources] = useState(DEFAULT_SOURCES);
  const [extras, setExtras] = useState([]);
  const [business, setBusiness] = useState({});
  // v4.2.6 — per-field validation errors drive the red borders.
  const [fieldErrors, setFieldErrors] = useState({});
  // v4.2.5 — required flags come from Business Settings → checkout_fields,
  // so the red asterisks match what the API actually enforces.
  const fieldRequired = (key) =>
    ((business?.checkout_fields || {})[key] || "off") === "required";
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    Promise.all([
      api.get("/vehicles").catch(() => ({ data: [] })),
      api.get("/settings/locations").catch(() => ({ data: [] })),
      api.get("/settings/public").catch(() => ({ data: {} })),
    ]).then(([v, l, s]) => {
      setVehicles(Array.isArray(v?.data) ? v.data : []);
      const locs = Array.isArray(l?.data) ? l.data : [];
      setLocations(locs);
      const cfg = (s?.data && Array.isArray(s.data.booking_sources) && s.data.booking_sources.length)
        ? s.data.booking_sources
        : DEFAULT_SOURCES;
      setSources(cfg);
      setBusiness(s?.data || {});
      // Prefer "Main Office" as the default pickup/return so admins don't
      // trip Airport-conditional required fields on a plain office pickup.
      const defaultLoc =
        locs.find((x) => (x.name || "").toLowerCase() === "main office")?.name
        || locs.find((x) => (x.location_type || "downtown") === "downtown")?.name
        || locs[0]?.name
        || "";
      setForm((f) => ({
        ...f,
        vehicle_id:      f.vehicle_id      || (v?.data?.[0]?.id || ""),
        pickup_location: f.pickup_location || defaultLoc,
        return_location: f.return_location || defaultLoc,
      }));
    });
  }, [open]);

  // Refetch extras whenever the selected vehicle changes so the list is
  // always scoped to that Car Group's allowed_extra_ids.
  useEffect(() => {
    if (!open || !form.vehicle_id) { setExtras([]); return; }
    api.get(`/extras?status=active&vehicle_id=${form.vehicle_id}`)
      .then((r) => setExtras(Array.isArray(r.data) ? r.data : []))
      .catch(() => setExtras([]));
    // Deselect any extras that no longer belong to the new vehicle's group.
    setForm((f) => {
      const allowed = new Set((extras || []).map((x) => x.id));
      if (f.addons.every((a) => allowed.has(a))) return f;
      return { ...f, addons: f.addons.filter((a) => allowed.has(a)) };
    });
  }, [open, form.vehicle_id]);

  // Live server-driven preview — debounced 300ms POST to /bookings/quote so
  // Subtotal/Extras/Tax/Total/Deposit/Balance Due always reflect the true
  // pricing engine (VAT-inclusive tax rate, seasonal rates, location fees,
  // vouchers, driver-age surcharges). Single source of truth — the same
  // envelope the create endpoint persists.
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  useEffect(() => {
    if (!form.vehicle_id || !form.pickup_date || !form.return_date) {
      setQuote(null); return;
    }
    if (new Date(form.return_date) <= new Date(form.pickup_date)) {
      setQuote(null); return;
    }
    const controller = new AbortController();
    setQuoteLoading(true);
    const t = setTimeout(() => {
      api.post("/bookings/quote", {
        vehicle_id:      form.vehicle_id,
        pickup_date:     form.pickup_date,
        return_date:     form.return_date,
        pickup_time:     form.pickup_time,
        return_time:     form.return_time,
        pickup_location: form.pickup_location,
        return_location: form.return_location,
        addons:          form.addons,
        driver_age:      form.driver_age === "" ? null : Number(form.driver_age),
      }, { signal: controller.signal })
        .then((r) => setQuote(r.data))
        .catch(() => setQuote(null))
        .finally(() => setQuoteLoading(false));
    }, 300);
    return () => { controller.abort(); clearTimeout(t); };
  }, [
    form.vehicle_id, form.pickup_date, form.return_date,
    form.pickup_time, form.return_time,
    form.pickup_location, form.return_location,
    JSON.stringify(form.addons), form.driver_age,
  ]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAddon = (id) =>
    setForm((f) => ({
      ...f,
      addons: f.addons.includes(id) ? f.addons.filter((a) => a !== id) : [...f.addons, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    // ── v4.2.6 · client-side pre-validation with inline field errors ──
    const errs = {};
    if (!String(form.full_name || "").trim()) errs.full_name = "This field is required";
    if (!String(form.email || "").trim())     errs.email = "This field is required";
    if (!String(form.phone || "").trim())     errs.phone = "This field is required";

    // Fields the operator marked as required in Business Settings.
    const cfg = business?.checkout_fields || {};
    for (const [key, status] of Object.entries(cfg)) {
      if (status !== "required") continue;
      if (key === "driver_age") continue;           // numeric, handled below
      if (!String(form[key] ?? "").trim()) errs[key] = "This field is required";
    }

    // driver_age is an integer column — never post an empty string.
    const ageRaw = String(form.driver_age ?? "").trim();
    const ageNum = ageRaw === "" ? null : Number(ageRaw);
    const ageInvalid = ageRaw !== "" && (!Number.isFinite(ageNum) || ageNum < 18 || ageNum > 99);
    if (fieldRequired("driver_age") && (ageRaw === "" || ageInvalid)) {
      errs.driver_age = "Please enter a valid driver age.";
    } else if (ageInvalid) {
      errs.driver_age = "Please enter a valid driver age.";
    }

    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setError(errs.driver_age && Object.keys(errs).length === 1
        ? errs.driver_age
        : "Please complete the highlighted required fields.");
      return;
    }

    if (!form.vehicle_id) { setError("Select a vehicle."); return; }
    if (!form.pickup_date || !form.return_date) {
      setError("Pick a pickup and return date."); return;
    }
    if (new Date(form.return_date) <= new Date(form.pickup_date)) {
      setError("Return date must be after pickup date."); return;
    }
    setSubmitting(true);
    try {
      const overrideVal = form.deposit_override === "" ? null : Number(form.deposit_override);
      const payload = {
        ...form,
        // Integer schema: send a number or null, never "".
        driver_age: ageNum !== null && Number.isFinite(ageNum) ? Math.trunc(ageNum) : null,
        payment_method: form.payment_method || null,
        deposit_override: (overrideVal !== null && !Number.isNaN(overrideVal) && overrideVal >= 0)
          ? overrideVal : null,
      };
      const { data } = await api.post("/bookings/admin/manual", payload);
      toast.success(`Reservation created · €${data.total.toFixed(2)}`);
      onCreated?.(data);
      setForm(EMPTY_FORM);
      setFieldErrors({});
      onClose();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="manual-booking-modal"
    >
      <div
        className="bg-white dark:bg-[#1F2937] text-[#0A0A0A] dark:text-[#F9FAFB] max-w-4xl w-full p-8 max-h-[95vh] overflow-y-auto rounded border border-black/10 dark:border-white/10 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Admin</p>
            <h2 className="font-display text-3xl tracking-tight mt-1">New reservation</h2>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-black dark:hover:text-white"
            aria-label="Close"
            data-testid="close-manual-modal"
          >
            <X size={20} />
          </button>
        </div>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-6 max-w-2xl">
          Create a booking manually on behalf of a customer (phone-in, walk-in, agency, hotel partner). Pricing is
          computed server-side using the same engine as the online flow.
        </p>

        {/* v4.2.6 — noValidate: our own inline validation owns the UX so
            the browser's native bubbles never pre-empt the red borders. */}
        <form onSubmit={submit} className="space-y-6" noValidate>
          {/* Customer */}
          <Section title="Customer">
            <Row>
              <Field label="Full name *">
                <Input value={form.full_name} onChange={(e) => update("full_name", e.target.value)} required testId="mb-fullname" error={fieldErrors.full_name} />
              </Field>
              <Field label="Email *">
                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required testId="mb-email" error={fieldErrors.email} />
              </Field>
              <Field label="Phone *">
                <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} required testId="mb-phone" error={fieldErrors.phone} />
              </Field>
              <Field label="ID / Passport" required={fieldRequired("id_passport")}>
                <Input value={form.id_passport} onChange={(e) => update("id_passport", e.target.value)} testId="mb-id" error={fieldErrors.id_passport} />
              </Field>
              <Field label="Driver license number" required={fieldRequired("license_number")}>
                <Input value={form.license_number} onChange={(e) => update("license_number", e.target.value)} testId="mb-license" error={fieldErrors.license_number} />
              </Field>
              <Field label="Driver age" required={fieldRequired("driver_age")}>
                <Input type="number" min="18" max="99" value={form.driver_age}
                  onChange={(e) => update("driver_age", e.target.value)} testId="mb-driver-age"
                  error={fieldErrors.driver_age} />
              </Field>
            </Row>
            {/* v4.0.3 — Dynamic Address / Country / City / ZIP / Notes
                rendered INSIDE the Customer card (previously lived below
                Rental). Two-column grid keeps parity with the fixed
                fields above. Conditional airport / port / hotel groups
                still render below Rental in their own card. */}
            <div className="mt-4">
              <CheckoutFields
                form={form}
                setForm={setForm}
                locations={locations}
                renderGroup="customer"
                hideBasics
                hideDriver
                errors={fieldErrors}
              />
            </div>
          </Section>

          {/* Rental */}
          <Section title="Rental">
            <Row>
              <Field label="Vehicle *">
                <Select
                  required
                  value={form.vehicle_id}
                  onChange={(e) => update("vehicle_id", e.target.value)}
                  testId="mb-vehicle"
                  disabled={vehicles.length === 0}
                >
                  <option value="">
                    {vehicles.length === 0 ? "No vehicles available" : "Select…"}
                  </option>
                  {vehicles.map((v) => {
                    const rate = v.has_duration_tiers ? v.display_rate : v.daily_rate;
                    return (
                      <option key={v.id} value={v.id}>
                        {v.brand} {v.name} — €{rate}/day
                      </option>
                    );
                  })}
                </Select>
              </Field>
              <Field label="Pickup location *">
                <Select value={form.pickup_location} onChange={(e) => update("pickup_location", e.target.value)} required testId="mb-pickup-loc">
                  {locations.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
                </Select>
              </Field>
              <Field label="Pickup date *">
                <DateInput value={form.pickup_date} onChange={(v) => update("pickup_date", v)} testId="mb-pickup-date" />
              </Field>
              <Field label="Pickup time">
                <Input type="time" value={form.pickup_time} onChange={(e) => update("pickup_time", e.target.value)} testId="mb-pickup-time" />
              </Field>
              <Field label="Return location *">
                <Select value={form.return_location} onChange={(e) => update("return_location", e.target.value)} required testId="mb-return-loc">
                  {locations.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
                </Select>
              </Field>
              <Field label="Return date *">
                <DateInput value={form.return_date} onChange={(v) => update("return_date", v)} testId="mb-return-date" />
              </Field>
              <Field label="Return time">
                <Input type="time" value={form.return_time} onChange={(e) => update("return_time", e.target.value)} testId="mb-return-time" />
              </Field>
            </Row>
            <div className="mt-4">
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 dark:text-neutral-400 mb-2">
                Extras & insurance
              </p>
              {extras.length === 0 ? (
                <p className="text-xs text-neutral-500 dark:text-neutral-400 italic">
                  No active extras — add some under <em>Rental Settings</em>.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="mb-extras-list">
                  {extras.map((x) => {
                    const on = form.addons.includes(x.id);
                    return (
                      <button
                        type="button"
                        key={x.id}
                        onClick={() => toggleAddon(x.id)}
                        data-testid={`mb-addon-${x.id}`}
                        title={`€${Number(x.price).toFixed(2)} ${x.pricing_type === "per_day" ? "per day" : "per reservation"}`}
                        className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                          on ? "bg-black text-white border-black dark:bg-white dark:text-black dark:border-white"
                             : "border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white"
                        }`}
                      >
                        {x.name}
                        <span className="ml-1 opacity-70">
                          €{Number(x.price).toFixed(0)}/{x.pricing_type === "per_day" ? "day" : "res"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </Section>

          {/* Location-conditional extra fields (airport / port / hotel).
              Renders directly BELOW the Rental card in its own dedicated
              container — mirrors the public checkout page exactly.
              v4.0.3 — Customer address group has moved INSIDE the
              Customer card; only conditional groups render here. */}
          <CheckoutFields
            form={form}
            setForm={setForm}
            locations={locations}
            renderGroup="conditional"
            hideBasics
            hideDriver
            errors={fieldErrors}
          />

          {/* v3.7.1 — Port pickup ferry details card. Name-based match so
              it works even when the location's `location_type` isn't set. */}
          <PortFerryCard
            pickupLocation={form.pickup_location}
            value={form.ferry_details}
            onChange={(next) => update("ferry_details", next)}
            testIdPrefix="mb"
          />

          {/* Pricing / payment */}
          <Section title="Payment & source">
            <Row>
              <Field label="Booking source">
                <Select value={form.source} onChange={(e) => update("source", e.target.value)} testId="mb-source">
                  {(Array.isArray(sources) ? sources : DEFAULT_SOURCES).map((s) => (
                    <option key={s} value={s}>
                      {s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Payment status">
                <Select value={form.payment_status} onChange={(e) => update("payment_status", e.target.value)} testId="mb-payment-status">
                  {PAYMENT_STATUSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </Select>
              </Field>
              <Field label="Payment method">
                <Select value={form.payment_method} onChange={(e) => update("payment_method", e.target.value)} testId="mb-payment-method">
                  {PAYMENT_METHODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </Select>
              </Field>
              <Field label="Initial booking status">
                <Select value={form.initial_status} onChange={(e) => update("initial_status", e.target.value)} testId="mb-initial-status">
                  <option value="confirmed">Confirmed</option>
                  <option value="pending_payment">Pending Payment</option>
                </Select>
              </Field>
              <Field label="Deposit received now (€, optional)">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder={
                    quote
                      ? (form.payment_status === "paid"
                          ? `${Number(quote.total || 0).toFixed(2)}`
                          : form.payment_status === "partial"
                            ? `${Number(quote.advance_deposit || 0).toFixed(2)}`
                            : "0.00")
                      : "auto"
                  }
                  value={form.deposit_override}
                  onChange={(e) => update("deposit_override", e.target.value)}
                  testId="mb-deposit-override"
                />
              </Field>
            </Row>
            {/* v4.0.3 — "Notes (internal)" retired: the Notes field now
                lives inside the Customer card via CheckoutFields. */}
          </Section>

          {quote && (
            <div
              className="bg-black/[0.02] dark:bg-white/[0.03] border border-black/10 dark:border-white/10 rounded p-4 text-sm space-y-1"
              data-testid="mb-preview"
            >
              <div className="flex justify-between"><span>Days</span><span>{quote.days}</span></div>
              <div className="flex justify-between"><span>Subtotal (rental)</span><span>€{Number(quote.subtotal || 0).toFixed(2)}</span></div>
              {Number(quote.addons_total || 0) > 0 && (
                <div className="flex justify-between"><span>Extras</span><span>€{Number(quote.addons_total).toFixed(2)}</span></div>
              )}
              {Number(quote.pickup_fee || 0) + Number(quote.return_fee || 0) + Number(quote.out_of_hours_fee || 0) > 0 && (
                <div className="flex justify-between text-neutral-500">
                  <span>Location & out-of-hours fees</span>
                  <span>€{(Number(quote.pickup_fee || 0) + Number(quote.return_fee || 0) + Number(quote.out_of_hours_fee || 0)).toFixed(2)}</span>
                </div>
              )}
              {Number(quote.voucher_discount || 0) > 0 && (
                <div className="flex justify-between text-emerald-700 dark:text-emerald-400">
                  <span>Voucher {quote.voucher_code}</span><span>− €{Number(quote.voucher_discount).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-neutral-500">
                <span>VAT ({business?.tax_rate_pct ?? 24}% included)</span>
                <span>€{Number(quote.taxes || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold pt-1 border-t border-black/10 dark:border-white/10 mt-1 text-base">
                <span>Estimated total</span>
                <span data-testid="mb-preview-total">€{Number(quote.total || 0).toFixed(2)}</span>
              </div>
              {quoteLoading && (
                <p className="text-[10px] text-neutral-500 italic pt-1" data-testid="mb-quote-loading">Recalculating…</p>
              )}
            </div>
          )}

          {/* Balance-due box — always BELOW the totals block. For a new
              admin booking the deposit received is either the admin's
              explicit override (Deposit received now field) OR the
              system-derived amount based on payment_status:
              `paid` → all covered, `partial` → advance deposit from
              settings, `unpaid`/`cod` → nothing. Balance = Total − Deposit. */}
          {quote && (() => {
            const total = Number(quote.total || 0);
            const overrideVal = form.deposit_override === "" ? null : Number(form.deposit_override);
            let paid;
            if (overrideVal !== null && !Number.isNaN(overrideVal) && overrideVal >= 0) {
              paid = Math.min(overrideVal, total);
            } else if (form.payment_status === "paid") {
              paid = total;
            } else if (form.payment_status === "partial") {
              paid = Number(quote.advance_deposit || 0);
            } else {
              paid = 0;
            }
            const balance = Math.max(total - paid, 0);
            if (balance <= 0) return null;
            const overrideActive = overrideVal !== null && !Number.isNaN(overrideVal);
            return (
              <div
                className="p-4 border border-amber-400/50 bg-amber-50 dark:bg-amber-900/20 rounded"
                data-testid="mb-balance-panel"
              >
                <p className="font-medium text-amber-900 dark:text-amber-200 text-sm">
                  Balance due at pickup: €{balance.toFixed(2)}
                </p>
                <p className="text-[11px] text-amber-800/80 dark:text-amber-300/70 mt-0.5">
                  {overrideActive
                    ? `Custom deposit €${paid.toFixed(2)} received · Total €${total.toFixed(2)}`
                    : form.payment_status === "partial"
                      ? `Deposit €${paid.toFixed(2)} charged now · Total €${total.toFixed(2)}`
                      : `No deposit received · Total €${total.toFixed(2)}`}
                </p>
              </div>
            );
          })()}

          {error && (
            <p className="text-red-600 dark:text-rose-400 text-sm" data-testid="mb-error">{error}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="border border-black/15 dark:border-white/15 px-5 py-2 text-sm hover:border-black dark:hover:border-white transition-colors rounded"
              data-testid="mb-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="bg-black text-white dark:bg-white dark:text-black px-5 py-2 text-sm hover:opacity-90 transition-opacity inline-flex items-center gap-2 disabled:opacity-50 rounded"
              data-testid="mb-submit"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {submitting ? "Creating…" : "Create reservation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded p-5">
      <h3 className="font-display text-lg mb-4">{title}</h3>
      {children}
    </div>
  );
}
function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">{children}</div>;
}
function Field({ label, required, children }) {
  // v4.2.5 — a trailing "*" in the label (or `required`) renders an
  // explicit red asterisk instead of a grey inline character.
  const text = typeof label === "string" ? label.replace(/\s*\*\s*$/, "") : label;
  const star = required || (typeof label === "string" && /\*\s*$/.test(label));
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 block mb-1">
        {text}
        {star && <span className="text-red-500 font-bold ml-0.5" aria-hidden="true">*</span>}
      </span>
      {children}
    </label>
  );
}
function Input(props) {
  const { testId, error, ...rest } = props;
  // v4.2.6 — the error state self-clears once a value is present, so the
  // red border disappears as the operator types.
  const showError = Boolean(error) && !String(rest.value ?? "").trim();
  return (
    <>
      <input
        {...rest}
        data-testid={testId}
        className={`w-full bg-transparent border-b py-1.5 outline-none text-sm ${
          showError
            ? "border border-red-500 focus:ring-red-500 bg-red-50/20 rounded px-2"
            : "border-black/15 dark:border-white/15 focus:border-black dark:focus:border-white"
        }`}
      />
      {showError && (
        <span className="block text-xs text-red-500 mt-1" data-testid={`${testId}-error`}>
          {error}
        </span>
      )}
    </>
  );
}
function Select({ testId, children, ...rest }) {
  return (
    <select
      {...rest}
      data-testid={testId}
      className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none focus:border-black dark:focus:border-white text-sm"
    >
      {children}
    </select>
  );
}
